import { AxiosResponse } from 'axios';

import { useQueryClient } from '@tanstack/react-query';
import {
  TypeCreateWorkApplicationsBirthDateVerifyRequest,
  TypeCreateWorkApplicationsBirthDateVerifyResponse,
} from '~/api/workApplications';
import { registerMockAPI } from '~/mocks/mockApis';

import { INVALID_BIRTH_DATE_ERROR_CODE, INVALID_GENDER_DIGIT_ERROR_CODE, UNDERAGE_ERROR_CODE } from '~/constants';
import { axiosInstance } from '~/services/fetchServiceV2';
import { TypeErrorFormat, requestHelper } from '~/utils/fetchHelper';

/**
 * @description 근무 지원 생년월일/성별 유효성 검사 api 요청에 사용되는 커스텀 훅
 */
export const useCheckValidBirthDate = () => {
  const queryClient = useQueryClient();

  const requestCheckValidBirthDate = async ({
    params,
    onSuccess,
    onError,
  }: {
    params: TypeCreateWorkApplicationsBirthDateVerifyRequest;
    onSuccess?: (res: TypeCreateWorkApplicationsBirthDateVerifyResponse) => void;
    onError?: (err: TypeErrorFormat) => void;
  }) => {
    const endpoint = '/work-applications/birth-date/verify';

    const body: TypeCreateWorkApplicationsBirthDateVerifyRequest = {};
    if (params.birthDate) body.birthDate = params.birthDate;
    if (params.genderDigit) body.genderDigit = params.genderDigit;

    const queryKey = ['verify', 'birthDate', body.birthDate ?? '', body.genderDigit ?? ''] as const;

    const query = queryClient.ensureQueryData<AxiosResponse<TypeCreateWorkApplicationsBirthDateVerifyResponse>>({
      queryKey,
      queryFn: () => axiosInstance.post(endpoint, body, { headers: { withoutToken: true } }),
      gcTime: 5 * 1000, // 5초
    });

    return requestHelper(() => query, onSuccess, onError, undefined, { showLoading: false });
  };

  return requestCheckValidBirthDate;
};

registerMockAPI<TypeCreateWorkApplicationsBirthDateVerifyResponse>({
  method: 'post',
  endpoint: '/work-applications/birth-date/verify',
  label: '근무 지원 정보/생년월일 형식 검증',
  scenarios: [
    {
      name: '성공',
      response: {
        code: '0000',
        message: '정상',
      },
    },
    {
      name: '생년월일 형식 오류 (400)',
      response: {
        code: INVALID_BIRTH_DATE_ERROR_CODE,
        message: '생년월일을 확인해주세요',
      },
      status: 400,
    },
    {
      name: '만 14세 미만 (400)',
      response: {
        code: UNDERAGE_ERROR_CODE,
        message: '만 14세 미만은 지원할 수 없어요',
      },
      status: 400,
    },
    {
      name: '성별 구분 코드 오류 (400)',
      response: {
        code: INVALID_GENDER_DIGIT_ERROR_CODE,
        message: '성별을 확인해주세요',
      },
      status: 400,
    },
    {
      name: '실패',
      response: {
        code: '500',
        message: '서버 내부 오류가 발생했습니다.',
      },
      status: 500,
    },
  ],
});
